import React from 'react';
import {
  Alert,
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TimelineEntryAttribution from '../../components/Timeline/TimelineEntryAttribution';
import NaturalCaptureComposerShell from '../../components/UI/NaturalCaptureComposerShell';
import LogDateField from '../../components/UI/LogDateField';
import LogTimeField from '../../components/UI/LogTimeField';
import SharedDateCalendar from '../../components/UI/SharedDateCalendar';
import { getTimelineEntryDate } from '../../services/timeline/dateUtils';
import colors from '../../assets/theme/colors';
import {
  formatDateForEditInput,
  formatTimeForEditInput,
  parseTimelineEditDate,
  parseTimelineEditTime,
  saveTimelineNarrativeNoteAndReprocess,
  updateTimelineNarrativeNote,
} from './timelineNoteEditService';

export const getTimelineEditInitialText = (entry = {}) => String(
  entry.text
  || entry.body
  || entry.content
  || entry.note
  || entry.notes
  || entry.description
  || entry.noteContextText
  || entry.sourceText
  || ''
).trim();

const entryNeedsReview = (entry = {}) => Boolean(
  entry?.metadata?.needsReview
  || entry?.metadata?.captureFirstReviewState === 'needs_review'
  || entry?.metadata?.captureFirst?.needsReview
  || entry?.reviewConfidence === 'review'
);

const formatDisplayDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return 'Select date';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const TimelineNoteEditSheet = ({
  open,
  child = null,
  entry = null,
  currentUserId = null,
  onClose,
  onSaved,
}) => {
  const [noteText, setNoteText] = React.useState('');
  const [localDate, setLocalDate] = React.useState(formatDateForEditInput);
  const [localTime, setLocalTime] = React.useState(formatTimeForEditInput);
  const [dateAnchorEl, setDateAnchorEl] = React.useState(null);
  const [savingAction, setSavingAction] = React.useState('');
  const [error, setError] = React.useState('');
  const saving = Boolean(savingAction);

  React.useEffect(() => {
    if (!open || !entry) {
      return;
    }

    const timestamp = getTimelineEntryDate(entry) || new Date();
    setNoteText(getTimelineEditInitialText(entry));
    setLocalDate(entry.localDate || formatDateForEditInput(timestamp));
    const timeSource = entry.timeSource || entry.metadata?.timeSource || '';
    const timeIsUncertain = timeSource === 'missing' || timeSource.startsWith('blocked_') || ['inferred', 'approximate'].includes(timeSource);
    setLocalTime(timeIsUncertain ? '' : (parseTimelineEditTime(entry.localTime || entry.time)?.storageValue || formatTimeForEditInput(timestamp)));
    setError('');
    setSavingAction('');
    setDateAnchorEl(null);
  }, [entry, open]);

  const dateValue = React.useMemo(() => (
    parseTimelineEditDate(localDate) || new Date()
  ), [localDate]);

  const handleDateChange = (nextDate) => {
    if (!nextDate || Number.isNaN(nextDate.getTime())) {
      return;
    }

    setLocalDate(formatDateForEditInput(nextDate));
    setDateAnchorEl(null);
  };

  const handleSave = async ({ reprocessWithAi = false } = {}) => {
    if (!entry?.id) {
      setError('This timeline record is missing an id.');
      return;
    }

    if (reprocessWithAi && !child?.id) {
      setError('This timeline record is missing a child id.');
      return;
    }

    const cleanText = noteText.trim();
    if (!cleanText) {
      setError('Add a note before saving.');
      return;
    }

    setSavingAction(reprocessWithAi ? 'refresh' : 'save');
    setError('');

    try {
      const saveParams = {
        entryId: entry.id,
        expectedRevision: entry.sourceRevision || null,
        text: cleanText,
        localDate,
        localTime,
        category: entry.category || entry.type || 'log',
        updatedBy: currentUserId,
        markReviewed: entryNeedsReview(entry),
      };
      const updatedEntry = reprocessWithAi
        ? await saveTimelineNarrativeNoteAndReprocess({
          ...saveParams,
          childId: child.id,
        })
        : await updateTimelineNarrativeNote(saveParams);
      onSaved?.(updatedEntry);
    } catch (saveError) {
      setError(saveError?.editSaved
        ? 'Your edit was saved, but Lifelog could not refresh it right now.'
        : (saveError?.message || 'We could not save this edit right now.'));
      setSavingAction('');
    }
  };

  const needsReview = entryNeedsReview(entry || {});

  return (
    <NaturalCaptureComposerShell
      open={open}
      onClose={saving ? undefined : onClose}
      title="Edit note"
      subtitle="Correct the date, time, or note."
      child={child}
      bodySx={{ pt: { xs: 1.3, md: 2 } }}
    >
      <Box
        sx={{
          p: { xs: 1.6, sm: 2 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(121, 137, 130, 0.2)',
          bgcolor: '#FFFFFF',
        }}
      >
        <Box sx={{ mb: 1.5 }}>
          <TimelineEntryAttribution entry={entry || {}} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <LogDateField
            label="Date"
            value={formatDisplayDate(dateValue)}
            onClick={(event) => setDateAnchorEl(event.currentTarget)}
            sx={{ flex: 1 }}
          />
          <LogTimeField
            label="Time"
            value={localTime}
            onChange={(event) => setLocalTime(event.target.value)}
            sx={{ flex: 1 }}
          />
        </Stack>

        <TextField
          fullWidth
          multiline
          minRows={5}
          value={noteText}
          onChange={(event) => setNoteText(event.target.value)}
          placeholder="Edit what happened..."
          disabled={saving}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 2.5,
              bgcolor: '#FFFFFF',
              fontSize: { xs: '1rem', sm: '1.05rem' },
              lineHeight: 1.45,
            },
          }}
        />

        {error ? (
          <Alert severity="error" sx={{ mt: 1.2 }}>
            {error}
          </Alert>
        ) : null}

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{
            mt: 1.5,
            position: 'sticky',
            bottom: 0,
            py: 1,
            bgcolor: '#FFFFFF',
            zIndex: 1,
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
            {needsReview ? 'Save your correction, then review is done for this record.' : 'Save updates this timeline record.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleSave({ reprocessWithAi: true })}
              disabled={saving || !noteText.trim()}
              sx={{
                minHeight: 48,
                borderRadius: '999px',
                px: 2.4,
                borderColor: colors.brand.deep,
                color: colors.brand.deep,
                fontWeight: 850,
                textTransform: 'none',
                '&:hover': {
                  borderColor: colors.brand.deep,
                  bgcolor: 'rgba(51, 86, 66, 0.06)',
                },
              }}
            >
              {savingAction === 'refresh' ? 'Saving and starting refresh...' : 'Save and refresh with AI'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveOutlinedIcon />}
              onClick={() => handleSave()}
              disabled={saving || !noteText.trim()}
              sx={{
                minHeight: 48,
                borderRadius: '999px',
                px: 3,
                bgcolor: colors.brand.deep,
                color: '#FFFFFF',
                fontWeight: 850,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: colors.brand.deep,
                },
              }}
            >
              {savingAction === 'save' ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <SharedDateCalendar
          value={dateValue}
          onChange={handleDateChange}
        />
      </Popover>
    </NaturalCaptureComposerShell>
  );
};

export default TimelineNoteEditSheet;
